import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATALOG_CACHE_KEY = "luviarx_catalog_cache_v1";

/**
 * CatalogContext — fetches the product catalog from /api/products on mount,
 * caches the result in localStorage so navigations stay snappy. All product-
 * related screens (PDP, cart, home, products list) read from this context
 * instead of importing a static JS array.
 */
const CatalogContext = createContext(null);

export const CatalogProvider = ({ children }) => {
  const [products, setProducts] = useState(() => {
    try {
      const raw = localStorage.getItem(CATALOG_CACHE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchCatalog = async () => {
    setError("");
    try {
      const { data } = await axios.get(`${API}/products`);
      if (Array.isArray(data)) {
        setProducts(data);
        try {
          localStorage.setItem(CATALOG_CACHE_KEY, JSON.stringify(data));
        } catch { /* quota — ignore */ }
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load catalog");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCatalog();
  }, []);

  const value = useMemo(() => ({
    products,
    loading,
    error,
    refresh: fetchCatalog,
  }), [products, loading, error]);

  return (
    <CatalogContext.Provider value={value}>
      {children}
    </CatalogContext.Provider>
  );
};

export const useCatalog = () => {
  const ctx = useContext(CatalogContext);
  if (!ctx) throw new Error("useCatalog must be used within CatalogProvider");
  return ctx;
};

/**
 * Convenience hook returning a single product by slug (or null).
 */
export const useProduct = (slug) => {
  const { products } = useCatalog();
  return products.find((p) => p.slug === slug) || null;
};
